// Drop-in replacement for the "pixi-spine" barrel package, which eagerly bundles the
// spine 3.7/3.8/4.1 runtimes together (~880 KB) so it can auto-detect a skeleton's
// spine-editor version at load time. Every game in this codebase ships spine 4.1-format
// assets today, so importing 3.7/3.8 is dead weight - this keeps the same
// detect-then-verify shape as the original barrel (so a future spine version is just
// another import + branch below), but only actually wires up @pixi-spine/runtime-4.1,
// cutting runtime-3.7 (202 KB) and runtime-3.8 (262 KB) out of the bundle entirely.
import { AnimationState, AnimationStateData, AtlasAttachmentLoader, Skeleton, SkeletonBinary, SkeletonData as RuntimeSkeletonData, SkeletonJson } from "@pixi-spine/runtime-4.1";
import { ISpineResource, SpineLoaderAbstract } from "@pixi-spine/loader-base";
import { BinaryInput, IAnimationState, IAnimationStateData, ISkeleton, ISkeletonData, ISkeletonParser, SpineBase, TextureAtlas } from "@pixi-spine/base";

export * from "@pixi-spine/base";

enum SpineVersion {
	Unknown = 0,
	Ver40 = 40,
	Ver41 = 41,
}

// Same detection rule the original pixi-spine barrel used: only the major.minor prefix
// of the exported skeleton's version string matters (e.g. "4.1.24" -> "4.1"). Spine 4.0
// and 4.1 both use @pixi-spine/runtime-4.1 (matches the original library's own mapping).
// To support another spine version later: add its runtime package as a dependency, add
// an entry to SpineVersion, and branch on it in the 3 spots below that call this.
function DetectSpineVersion(version: string): SpineVersion {
	const majorMinor = (version ?? "").substr(0, 3);
	if (majorMinor === "4.0") return SpineVersion.Ver40;
	if (majorMinor === "4.1") return SpineVersion.Ver41;
	return SpineVersion.Unknown;
}

function AssertSupportedVersion(version: SpineVersion, rawVersion: string): void {
	if (version !== SpineVersion.Ver40 && version !== SpineVersion.Ver41) {
		console.error(`Cant detect version of spine model "${rawVersion}" - this build only supports spine 4.0/4.1 (see PixiSpineRuntime.ts)`);
	}
}

// Matches pixi-spine's own barrel Spine class shape exactly (SpineBase typed against the
// loose I* interfaces, not concrete runtime classes - every call site in this codebase
// already expects that looser typing), but constructs runtime-4.1 classes directly
// instead of picking a runtime via version auto-detection.
export class Spine extends SpineBase<ISkeleton, ISkeletonData, IAnimationState, IAnimationStateData> {
	createSkeleton(spineData: ISkeletonData): void {
		AssertSupportedVersion(DetectSpineVersion(spineData.version), spineData.version);
		// spineData is always produced by SkeletonJsonParser/SkeletonBinaryParser below,
		// which already construct it via runtime-4.1's own SkeletonJson/SkeletonBinary -
		// so it's really a runtime-4.1 SkeletonData underneath the loose ISkeletonData type.
		const runtimeSpineData = spineData as unknown as RuntimeSkeletonData;
		this.skeleton = new Skeleton(runtimeSpineData);
		this.skeleton.updateWorldTransform();
		const stateData = new AnimationStateData(runtimeSpineData);
		this.stateData = stateData;
		this.state = new AnimationState(stateData);
	}
}

class SkeletonJsonParser implements ISkeletonParser {
	scale = 1;

	readSkeletonData(atlas: TextureAtlas, dataToParse: any): ISkeletonData {
		AssertSupportedVersion(DetectSpineVersion(dataToParse?.skeleton?.spine), dataToParse?.skeleton?.spine);
		const parser = new SkeletonJson(new AtlasAttachmentLoader(atlas));
		parser.scale = this.scale;
		return parser.readSkeletonData(dataToParse);
	}
}

class SkeletonBinaryParser implements ISkeletonParser {
	scale = 1;

	private ReadVersionOldFormat(dataToParse: Uint8Array): string {
		const input = new BinaryInput(dataToParse);
		try {
			input.readString();
			return input.readString() || "";
		} catch {
			return "";
		}
	}

	private ReadVersionNewFormat(dataToParse: Uint8Array): string {
		const input = new BinaryInput(dataToParse);
		input.readInt32();
		input.readInt32();
		try {
			return input.readString() || "";
		} catch {
			return "";
		}
	}

	readSkeletonData(atlas: TextureAtlas, dataToParse: Uint8Array): ISkeletonData {
		const oldFormatVersion = DetectSpineVersion(this.ReadVersionOldFormat(dataToParse));
		const newFormatVersion = DetectSpineVersion(this.ReadVersionNewFormat(dataToParse));
		if (oldFormatVersion === SpineVersion.Unknown && newFormatVersion === SpineVersion.Unknown) {
			console.error(`Unsupported version of spine binary model - this build only supports spine 4.0/4.1 (see PixiSpineRuntime.ts)`);
		}
		const parser = new SkeletonBinary(new AtlasAttachmentLoader(atlas));
		parser.scale = this.scale;
		return parser.readSkeletonData(dataToParse);
	}
}

class SpineLoader extends SpineLoaderAbstract<ISkeletonData> {
	createJsonParser(): ISkeletonParser {
		return new SkeletonJsonParser();
	}

	createBinaryParser(): ISkeletonParser {
		return new SkeletonBinaryParser();
	}

	parseData(parser: ISkeletonParser, atlas: TextureAtlas, dataToParse: any): ISpineResource<ISkeletonData> {
		const parserCast = parser as SkeletonJsonParser | SkeletonBinaryParser;
		return {
			spineData: parserCast.readSkeletonData(atlas, dataToParse),
			spineAtlas: atlas
		};
	}
}

new SpineLoader().installLoader();
